import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { Fragment, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AccountDetailsExplanation } from "@common/Layout/AccountDetailsExplanation";
import { AccountDetailsLayout } from "@common/Layout/AccountDetailsLayout";
import { BRAND_NAME, PORT_IN_STATUS_NOT_FOUND, TITLE_INHERIT_STYLE } from "@constants";
import moveMyNumber from "@static/img/portin/moveMyNumber.png";
import { PortInEdit } from "@features/PortIn/PortInEdit";
import { PortInDetails } from "@features/PortIn/PortInDetails";
import { PortInAction } from "@features/PortIn/PortInAction";
import { PortInState } from "@features/PortIn/interface/IPortIn";
import { useGetPortIn } from "@features/PortIn/hook/useGetPortIn";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";

export const PortInPage = () => {
    const { selectedService } = useContext(SelectedServiceContext);
    const { t } = useTranslation(["accountDetails", "portinPage"]);

    const {
        getPortInState: { error, isLoading },
        getPortIn,
        setIsPortOpen,
        isPortOpen,
        portInState,
        previousPhoneNumber,
        portInDetails,
    } = useGetPortIn();

    useEffect(() => {
        getPortInDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedService]);

    const hasDetails = portInState === PortInState.SCHEDULED || portInState === PortInState.IN_PROGRESS;
    const portInError = error && error.errorCode !== PORT_IN_STATUS_NOT_FOUND ? error : undefined;

    const handlePortIn = () => setIsPortOpen(true);
    const getPortInDetails = () => {
        if (selectedService) {
            getPortIn(selectedService.id);
        }
    };

    const closePortIn = () => {
        setIsPortOpen(false);
    };

    return (
        <AccountDetailsLayout
            requireNumberSelection
            selectYourNumberTitle={t("portinPage:wantMoveNumber")}
            title={t("accountDetails:moveNumber")}
        >
            <Grid container direction="column" spacing={4}>
                <Grid item>
                    <AccountDetailsExplanation
                        alt="move my number"
                        src={moveMyNumber}
                        texts={[t("portinPage:portInStep1"), t("portinPage:portInStep2"), t("portinPage:portInState3")]}
                        title={t("portinPage:portInExplanationTitle")}
                    />
                </Grid>
                {selectedService && (
                    <Grid item>
                        <Card elevation={2}>
                            <CardHeader
                                className="card-header card-header__right"
                                title={t("portinPage:moveStatus")}
                                titleTypographyProps={TITLE_INHERIT_STYLE}
                            />
                            <CardContent>
                                <div className="card-header__circle" />
                                <HandleErrorAndLoading
                                    error={portInError}
                                    isLoading={isLoading}
                                    retryFetch={getPortInDetails}
                                >
                                    {isPortOpen && (
                                        <PortInEdit
                                            closePortIn={closePortIn}
                                            getPortIn={getPortIn}
                                            portInDetails={portInDetails}
                                            previousPhoneNumber={previousPhoneNumber}
                                            selectedService={selectedService}
                                        />
                                    )}
                                    {!isPortOpen && (
                                        <Fragment>
                                            {portInState === PortInState.INITIAL && (
                                                <Grid container justify="center" spacing={4}>
                                                    <Grid item xs={12}>
                                                        <Typography align="center" variant="body1">
                                                            {t("portinPage:scheduleMoveNumber")}
                                                            <br />
                                                            {t("portinPage:replacePreviousNumber", {
                                                                brand: BRAND_NAME,
                                                                previousNumber: selectedService.name,
                                                            })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Button
                                                            color="secondary"
                                                            size="large"
                                                            variant="outlined"
                                                            onClick={handlePortIn}
                                                        >
                                                            {t("portinPage:scheduleNow")}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            )}
                                            {hasDetails && (
                                                <Grid container justify="center" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <PortInDetails
                                                            portInDateTime={portInDetails?.portStartDateTime}
                                                            portInState={portInState}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <PortInAction
                                                            getPortInDetails={getPortInDetails}
                                                            handleEditPortIn={handlePortIn}
                                                            portInId={portInDetails?.mnpId}
                                                            portInState={portInState}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Fragment>
                                    )}
                                </HandleErrorAndLoading>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </AccountDetailsLayout>
    );
};
